import {Component, effect, Inject, Signal, TrackByFunction} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {Mutation, StartWorkflowVodInput} from '../../../types-workflow';

import {START_WORKFLOW_VOD_DEFAULT} from '../../../queries-workflow';
import {forkJoin} from 'rxjs';
import {Router} from '@angular/router';
import {VodRepackagingFormatSpecificComponent} from '../vod-repackaging-format-specific/vod-repackaging-format-specific.component';
import {BasicPoitemVodInfoComponent} from '../basics-poitem-vod-info/basic-poitem-vod-info.component';
import {clean_datetime} from '../../utils';
import {poNumberValidator} from '../../name-limits';
import {PO_DESTINATIONS, PoDestination} from '../../../target_systems';
import {Organization} from '../../../types-repository';
import {Po2024Service} from '../../../services/po-2024.service';

import {DoButtonComponent} from '../../do-button/do-button.component';
import {ContextService} from '../../../services/context.service';
import {reportInvalid} from '../form-messages';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {ProcessService} from '../../../services/process.service';


// Same S3 delivery option the display-interactive workflow offers (see
// VodDisplayInteractivePoComponent), so repackaged output can be delivered to Display
// Interactive from any organization. It is the default destination for repackaging.
const DISPLAY_INTERACTIVE_DESTINATION: PoDestination = {
  display_name: 'Display Interactive (S3)',
  destination_id: 'displayinteractive'
};
const DEFAULT_CDN_DESTINATION: PoDestination = {display_name: 'Default CDN', destination_id: 'vod'};

@Component({
  selector: 'app-vod-repackaging-po',
  templateUrl: './vod-repackaging-po.component.html',
  styleUrls: ['./vod-repackaging-po.component.css'],
  imports: [
    FormFooterComponent,
    ReactiveFormsModule,
    BasicPoitemVodInfoComponent,
    VodRepackagingFormatSpecificComponent,]
})
export class VodRepackagingPoComponent {

  organization: Signal<Organization>;
  poDestinations: PoDestination[] = []
  poFormGroup: FormGroup;

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  /** Set once a submit was refused: from then on invalid PO items are outlined. */
  submitted = false;

  constructor(
    private apollo: Apollo,
    @Inject('vod_repackaging') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    private banner: BannerService,
    contextService: ContextService,
    private processService: ProcessService,
    private router: Router
  ) {
    this.organization = contextService.organization;

    this.poFormGroup = formBuilder.group({
      po_number: [clean_datetime(new Date()), [Validators.required, poNumberValidator(form => this.composePoName(form))]],
      po_destination: [null, Validators.required],
      auto_publish: [true, Validators.required],
      poItems: formBuilder.array([]),
    });

    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        savedForm.poItems.forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(poItem)));
        this.poFormGroup.patchValue(savedForm, {emitEvent: false})
      }
    });

    effect(() => {
      const org = this.organization();
      if (!org) return;
      // The organization is part of po_name, so the Project ID's limits depend on it.
      this.poFormGroup.controls['po_number'].updateValueAndValidity();

      // Display Interactive is the pre-selected default (it is what repackaging is used for), the
      // Default CDN comes next, and the organization's own destination — if it has one that is not
      // already Display Interactive — is offered last.
      const ownDestination: PoDestination =
        PO_DESTINATIONS.hasOwnProperty(org.legacy_name) && org.legacy_name !== DISPLAY_INTERACTIVE_DESTINATION.destination_id
          ? {display_name: org.legacy_name, destination_id: org.legacy_name}
          : null;

      this.poDestinations = [
        DISPLAY_INTERACTIVE_DESTINATION,
        DEFAULT_CDN_DESTINATION,
        ...(ownDestination ? [ownDestination] : []),
      ];
      this.poFormGroup.controls['po_destination'].setValue(DISPLAY_INTERACTIVE_DESTINATION.destination_id);
    });

    this.poService.poItemToAdd$.subscribe(data => {
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(
          Object.assign(
            {},
            {
              basics: BasicPoitemVodInfoComponent.DEFAULTS,
              format_specific_data: VodRepackagingFormatSpecificComponent.DEFAULTS
            },
            data)))
      }
    });
    this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    });
  }

  compare_destination_id(o1: any, o2: any) {
    return o1?.destination_id === o2?.destination_id;
  }

  /** The `po_name` this form submits — the single place it is composed. */
  private composePoName(form: AbstractControl): string {
    return `${this.organization()?.id ?? ''}_${(form.get('po_number')?.value ?? '').trim()}`;
  }

  submit_po(button: DoButtonComponent) {
    const organization = this.organization()
    if (!this.poFormGroup.valid) {
      this.submitted = true;
      // Shows every field's error, also those never touched (castlabs-ui gates them on .invalid).
      this.poFormGroup.markAllAsTouched();
      reportInvalid(button, this.poFormGroup, 'vod-repackaging-form');
      return
    }
    const workflows = []
    for (const poItem of this.poFormGroup.value.poItems) {
      workflows.push(this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_VOD_DEFAULT,
        variables: {
          input_brefix: poItem.basics.input_brefix,
          po_item_id: poItem.basics.po_item_id.trim(),
          po_name: this.composePoName(this.poFormGroup),
          po_destination: this.poFormGroup.value.po_destination,
          vtk_template: 'vod-display-interactive-repack',
          auto_publish: this.poFormGroup.value.auto_publish,
          format_specific_data: JSON.stringify(poItem.format_specific_data)
        } satisfies StartWorkflowVodInput

      }).pipe(
        map(d => ({
          process: d.data.start_workflow_vod_default,
          po_item_id: poItem.basics.po_item_id.trim()
        }))))
    }

    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('All encodes started successfully.');
          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} encoded.`,
              failureMessage: `Failed to encode ${workflow.po_item_id}.`,
              link: ['/#/o', organization.id, 'workflows',
                organization.id,
                `${organization.id}_${this.poFormGroup.value.po_number}`].join('/')
            })
          })
        },
        error: err => {
          button.done('Workflow submission failed: ' + err, {severity: 'cl-alert-danger'});
        }
      }
    )
  }

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.value)
  }

}
